<template>
  <div class="relative inline-block text-left">
    <button
      ref="trigger"
      type="button"
      class="flex items-center gap-3 rounded border border-blue-400 bg-white p-2 text-base font-semibold text-blue-500 transition focus:outline-none focus-visible:outline-none focus-visible:ring-0"
      @click="toggle"
    >
      <Icon icon-name="BlueCalendar"  />
      <span class="min-w-[100px] text-left text-sm font-bold text-primary-600">{{ activeLabel }}</span>
    </button>

    <transition name="fade">
      <div
        v-if="isOpen"
        :class="['absolute z-20 mt-2 w-96 rounded border border-slate-200 bg-white shadow-lg', alignmentClass]"
        @click.stop
      >

        <div class="max-h-[340px] overflow-y-auto px-4 py-3">
          <ul class="space-y-2 text-sm text-slate-700">
            <li v-for="preset in presets" :key="preset.value">
              <label
                class="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 transition hover:bg-slate-50"
              >
                <input
                  class="h-4 w-4 text-blue-500 focus:ring-blue-500"
                  type="radio"
                  name="date-preset"
                  :value="preset.value"
                  v-model="tempPreset"
                />
                <span>{{ preset.label }}</span>
              </label>
            </li>
          </ul>

          <div v-if="tempPreset === 'custom'" class="mt-4 space-y-2">
            <div class="grid grid-cols-2 gap-3">
              <label class="flex flex-col gap-1 text-xs font-medium text-slate-500">
                <span>From</span>
                <input
                  type="date"
                  v-model="tempRange.from"
                  class="rounded-lg border border-slate-200 px-2 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-0"
                />
              </label>
              <label class="flex flex-col gap-1 text-xs font-medium text-slate-500">
                <span>To</span>
                <input
                  type="date"
                  v-model="tempRange.to"
                  class="rounded-lg border border-slate-200 px-2 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-0"
                />
              </label>
            </div>
            <p v-if="customError" class="text-xs font-medium text-red-500">
              {{ customError }}
            </p>
          </div>
        </div>

        <footer class="flex items-center justify-between gap-3 border-t border-slate-100 px-4 py-3">
          <button
            type="button"
            class="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:shadow-none"
            @click="handleCancel"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-lg bg-blue-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-600 focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:shadow-none disabled:cursor-not-allowed disabled:bg-blue-200"
            :disabled="isApplyDisabled"
            @click="handleApply"
          >
            Apply
          </button>
        </footer>
      </div>
    </transition>
  </div>
</template>

<script>
import Icon from "./core/Icon.vue";

const PRESETS = [
  { value: "till-date", label: "Till Date" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this-week", label: "This Week" },
  { value: "this-month", label: "This Month" },
  { value: "this-year", label: "This Year" },
  { value: "custom", label: "Custom" }
];

function formatRange(range) {
  if (!range.from || !range.to) return "Custom";
  const { from, to } = range;
  return `${from} – ${to}`;
}

export default {
  name: "DateFilter",
  components: {
    Icon
  },
  props: {
    value: {
      type: Object,
      default: () => ({ preset: null, range: { from: "", to: "" } })
    },
    alignment: {
      type: String,
      default: "left",
      validator: (val) => ["left", "right"].includes(val)
    }
  },
  data() {
    return {
      isOpen: false,
      presets: PRESETS,
      activePreset: this.value?.preset ?? null,
      activeRange: {
        from: this.value?.range?.from || "",
        to: this.value?.range?.to || ""
      },
      tempPreset: this.value?.preset ?? null,
      tempRange: {
        from: this.value?.range?.from || "",
        to: this.value?.range?.to || ""
      },
      customError: ""
    };
  },
  computed: {
    isApplyDisabled() {
      if (this.tempPreset !== "custom") return false;
      return !this.tempRange.from || !this.tempRange.to;
    },
    activeLabel() {
      const preset = this.presets.find((p) => p.value === this.activePreset);
      if (!preset) {
        return "Date";
      }
      if (preset.value === "custom") {
        return formatRange(this.activeRange);
      }
      return preset.label;
    },
    alignmentClass() {
      return this.alignment === "right" ? "right-0" : "left-0";
    }
  },
  watch: {
    value: {
      handler(newVal) {
        if (!newVal) return;
        this.activePreset = newVal.preset ?? null;
        this.activeRange = {
          from: newVal.range?.from || "",
          to: newVal.range?.to || ""
        };
        this.tempPreset = this.activePreset ?? null;
        this.tempRange = { ...this.activeRange };
      },
      deep: true
    }
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
      this.customError = "";
      if (this.isOpen) {
        this.tempPreset = this.activePreset || null;
        this.tempRange = { ...this.activeRange };
        document.addEventListener("click", this.onOutsideClick);
      } else {
        document.removeEventListener("click", this.onOutsideClick);
      }
    },
    onOutsideClick(event) {
      if (!this.$refs.trigger) return;
      if (this.$el.contains(event.target)) return;
      this.close();
    },
    close() {
      this.isOpen = false;
      document.removeEventListener("click", this.onOutsideClick);
      this.customError = "";
    },
    handleCancel() {
      this.tempPreset = this.activePreset;
      this.tempRange = { ...this.activeRange };
      this.close();
      this.$emit("cancel");
    },
    normalizeSelection(selection = {}) {
      const preset = selection.preset ?? null;
      const range = selection.range || {};

      if (!preset) {
        return {
          preset: null,
          range: {
            from: range.from || "",
            to: range.to || "",
          },
        };
      }

      return {
        preset,
        range: this.computeRangeForPreset(preset, range),
      };
    },
    computeRangeForPreset(preset, range = {}) {
      const today = new Date();
      const todayFormatted = this.formatDateForApi(today);

      switch (preset) {
        case "today":
          return { from: todayFormatted, to: todayFormatted };
        case "yesterday": {
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          const formatted = this.formatDateForApi(yesterday);
          return { from: formatted, to: formatted };
        }
        case "this-week": {
          const startOfWeek = new Date();
          const weekday = startOfWeek.getDay();
          const diff = (weekday + 6) % 7;
          startOfWeek.setDate(startOfWeek.getDate() - diff);

          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 6);

          return {
            from: this.formatDateForApi(startOfWeek),
            to: this.formatDateForApi(endOfWeek),
          };
        }
        case "this-month": {
          const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          return {
            from: this.formatDateForApi(startOfMonth),
            to: this.formatDateForApi(endOfMonth),
          };
        }
        case "this-year": {
          const startOfYear = new Date(today.getFullYear(), 0, 1);
          const endOfYear = new Date(today.getFullYear(), 11, 31);
          return {
            from: this.formatDateForApi(startOfYear),
            to: this.formatDateForApi(endOfYear),
          };
        }
        case "till-date":
          return {
            from: range.from || "",
            to: todayFormatted,
          };
        case "custom":
          return {
            from: range.from || "",
            to: range.to || "",
          };
        default:
          return {
            from: range.from || "",
            to: range.to || "",
          };
      }
    },
    formatDateForApi(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    handleApply() {
      if (this.tempPreset === "custom" && this.isApplyDisabled) {
        this.customError = "Select both start and end dates.";
        return;
      }
      this.customError = "";
      this.activePreset = this.tempPreset === "custom" && !this.tempRange.from && !this.tempRange.to
        ? null
        : this.tempPreset;
      this.activeRange = { ...this.tempRange };
      const normalized = this.normalizeSelection({
        preset: this.activePreset,
        range: { ...this.activeRange }
      });
      this.$emit("input", normalized);
      this.$emit("apply", normalized);
      this.close();
    }
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onOutsideClick);
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
